/**
 * First-connect orientation coach-mark (Workstream C, variant C2b).
 *
 * A small, quiet, dismissible callout anchored under the main toolbar near the
 * New Query button. It names the surface the bridge landed on (Table Explorer),
 * explains what just ran, and points at the "New" (query) toolbar button for
 * custom SQL — the copy must match the button's visible label. It is not a
 * modal — no overlay, does not block interaction. Shown at most once per
 * install (the bridge is one-shot); dismissed by ✕, by clicking New Query, or
 * by the session ending.
 */

import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useFirstConnectCoachStore, dismissFirstConnectCoach } from './firstConnectCoachStore';

export function FirstConnectCoach() {
  const visible = useFirstConnectCoachStore((s) => s.visible);
  const tableName = useFirstConnectCoachStore((s) => s.tableName);

  if (!visible) return null;

  return (
    <div
      data-testid="first-connect-coach"
      role="status"
      className={cn(
        'absolute left-2 top-full z-50 mt-1 w-[19rem] max-w-[calc(100vw-1rem)]',
        'rounded-md border border-border bg-popover px-3 py-2.5 text-popover-foreground',
        'shadow-lg animate-first-connect-coach-in'
      )}
    >
      <button
        type="button"
        data-testid="first-connect-coach-close"
        aria-label="Dismiss"
        onClick={() => dismissFirstConnectCoach('close')}
        className="absolute right-1.5 top-1.5 rounded-sm p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
      >
        <X className="h-3.5 w-3.5" />
      </button>

      <p className="pr-4 text-[12.5px] leading-relaxed">
        <strong className="font-semibold">This is Table Explorer</strong>
        {tableName ? (
          <>
            {' '}&mdash; your <strong className="font-semibold">{tableName}</strong> table, first 1,000 rows.
          </>
        ) : (
          <>{' '}&mdash; the first 1,000 rows of your table.</>
        )}{' '}
        Filter, sort, and edit inline.
      </p>
      <p className="mt-1 text-[12.5px] leading-relaxed">
        Want to write custom SQL? Click <strong className="font-semibold">New</strong> to open a query editor.
      </p>
      <p className="mt-1.5 text-[11px] leading-snug text-muted-foreground">
        Tip: right-click any table in the sidebar &rarr; Edit top 200 rows reopens this view.
      </p>
    </div>
  );
}
